(() => {
  if (window.__faogoUpperInitialized) return;
  window.__faogoUpperInitialized = true;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakSet();
  const watchPause = (videos) => {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, intersectionRatio }) => {
        if (intersectionRatio < 0.15 && !target.paused) target.pause();
      });
    }, { threshold: [0, 0.15, 0.5] });
    videos.forEach(video => observer.observe(video));
  };

  const initFilm = (root) => {
    const film = root.querySelector('[data-faogo-film]');
    if (!film) return;
    const video = film.querySelector('video');
    const button = film.querySelector('button');
    button.addEventListener('click', () => {
      if (!video.src) video.src = matchMedia('(max-width: 700px)').matches ? film.dataset.mobileVideo : film.dataset.desktopVideo;
      video.muted = true;
      video.play().catch(() => {});
    });
    video.addEventListener('play', () => film.classList.add('is-playing'));
    video.addEventListener('pause', () => film.classList.remove('is-playing'));
    watchPause([video]);
  };

  const initProof = (root) => {
    const proof = root.querySelector('.faogo-proof');
    if (!proof) return;
    const tabs = [...proof.querySelectorAll('[data-proof-tab]')];
    const panels = [...proof.querySelectorAll('[data-proof-panel]')];
    const videos = panels.map(panel => panel.querySelector('video'));
    const setCurrent = (index, scroll = false) => {
      tabs.forEach((tab, i) => { tab.classList.toggle('is-active', i === index); tab.setAttribute('aria-selected', String(i === index)); });
      if (scroll && matchMedia('(max-width: 700px)').matches) panels[index]?.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'nearest', inline: 'center' });
    };
    tabs.forEach((tab, i) => tab.addEventListener('click', () => setCurrent(i, true)));
    videos.forEach((video, i) => video.addEventListener('play', () => { videos.forEach((other, j) => { if (j !== i) other.pause(); }); setCurrent(i); }));
    const films = proof.querySelector('.faogo-proof__films');
    if (films && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (entry.isIntersecting && matchMedia('(max-width: 700px)').matches) setCurrent(Number(entry.target.dataset.proofPanel)); });
      }, { root: films, threshold: 0.6 });
      panels.forEach(panel => observer.observe(panel));
    }
    watchPause(videos);
  };

  const initScenes = (root) => {
    const section = root.querySelector('[data-faogo-scenes]');
    if (!section) return;
    const buttons = [...section.querySelectorAll('[data-faogo-scene]')];
    const visual = section.querySelector('.faogo-scenes__visual');
    const media = section.querySelector('[data-scene-media]');
    const desktopImage = section.querySelector('[data-scene-desktop-image]');
    const mobileImage = section.querySelector('[data-scene-mobile-image]');
    const placeholder = section.querySelector('[data-scene-placeholder]');
    let timer;
    const show = (button, index) => {
      section.dataset.activeScene = button.dataset.faogoScene;
      buttons.forEach(item => { const active = item === button; item.classList.toggle('is-active', active); item.setAttribute('aria-selected', String(active)); });
      if (matchMedia('(max-width: 700px)').matches) button.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      section.querySelector('[data-scene-count]').textContent = `${String(index + 1).padStart(2, '0')} / 04`;
      section.querySelector('[data-scene-title]').textContent = button.dataset.title;
      section.querySelector('[data-scene-description]').textContent = button.dataset.description;
      clearTimeout(timer);
      visual.classList.add('is-changing');
      timer = setTimeout(() => {
        const desktop = button.dataset.desktop;
        const mobile = button.dataset.mobile || desktop;
        const empty = !desktop;
        visual.classList.toggle('is-empty', empty);
        media.hidden = empty;
        placeholder.hidden = !empty;
        if (!empty) {
          const alt = `FAOGO blower in a ${button.dataset.title} cleaning scene`;
          desktopImage.src = desktop;
          desktopImage.alt = alt;
          mobileImage.src = mobile;
          mobileImage.alt = alt;
        }
        else section.querySelector('[data-placeholder-name]').textContent = button.dataset.title;
        visual.classList.remove('is-changing');
      }, reducedMotion.matches ? 0 : 140);
    };
    buttons.forEach((button, index) => button.addEventListener('click', () => show(button, index)));
  };

  const initDesign = (root) => {
    const section = root.querySelector('[data-faogo-design]');
    if (!section) return;
    const buttons = [...section.querySelectorAll('[data-faogo-detail]')];
    const photo = section.querySelector('[data-design-photo]');
    const stage = section.querySelector('.faogo-design__stage');
    const hotspot = section.querySelector('[data-design-hotspot]');
    const picture = section.querySelector('[data-design-picture]');
    const mobileSource = section.querySelector('[data-design-mobile-source]');
    const image = section.querySelector('[data-design-image]');
    const title = section.querySelector('[data-design-title]');
    const copy = section.querySelector('[data-design-copy]');
    const current = section.querySelector('.faogo-design__current');
    let timer;
    buttons.forEach(button => {
      button.addEventListener('click', () => {
      buttons.forEach(item => { const active = item === button; item.classList.toggle('is-active', active); item.setAttribute('aria-selected', String(active)); });
      section.classList.remove('is-default');
      section.dataset.designState = button.dataset.faogoDetail;
      title.textContent = button.dataset.title;
      copy.textContent = button.dataset.copy;
      current.classList.remove('is-empty');
      clearTimeout(timer);
      stage.classList.add('is-changing');
      timer = setTimeout(() => {
        image.src = button.dataset.desktop;
        mobileSource.srcset = button.dataset.mobile || button.dataset.desktop;
        picture.dataset.state = button.dataset.faogoDetail;
        image.alt = `FAOGO cordless turbo blower, ${button.dataset.title.toLowerCase()} view`;
        hotspot.style.setProperty('--hotspot-x', button.dataset.hotspotX);
        hotspot.style.setProperty('--hotspot-y', button.dataset.hotspotY);
        stage.classList.remove('is-changing');
      }, reducedMotion.matches ? 0 : 210);
    });
    });
    section.querySelector('.faogo-design__choices').addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const index = buttons.indexOf(document.activeElement);
      if (index < 0) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
      buttons[next].focus();
      buttons[next].click();
    });
  };

  const init = (context = document) => context.querySelectorAll('[data-faogo-upper]').forEach(root => {
    if (seen.has(root)) return;
    seen.add(root);
    initFilm(root); initProof(root); initScenes(root); initDesign(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
